/**
 * @typedef {Object} CacheEntry
 * @property {any} data
 * @property {number} timestamp
 * @property {number} ttlMinutes
 */

class CacheManager {
  constructor() {
    /**
     * @type {Map<string, CacheEntry>}
     */
    this.cache = new Map()
    
    this.defaultTTL = {
      filters: 60,
      productsList: 2,
      productDetail: 1
    }
  }

  /**
   * Generate cache key
   * @param {string} endpoint
   * @param {Object} [params]
   * @returns {string}
   */
  generateKey(endpoint, params = {}) {
    const paramStr = Object.entries(params)
      .filter(([, v]) => v !== null && v !== undefined)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}=${JSON.stringify(v)}`)
      .join('&')
    
    return paramStr ? `${endpoint}:${paramStr}` : endpoint
  }

  /**
   * @private
   */
  _generateKey(endpoint, params = {}) {
    return this.generateKey(endpoint, params)
  }

  /**
   * Check if cache entry is still valid
   * @private
   * @param {CacheEntry} entry
   * @returns {boolean}
   */
  _isValid(entry) {
    const now = Date.now()
    const ageMinutes = (now - entry.timestamp) / 1000 / 60
    return ageMinutes < entry.ttlMinutes
  }

  /**
   * Set cache entry
   * @param {string} key
   * @param {any} data
   * @param {number} [ttlMinutes]
   */
  set(key, data, ttlMinutes = 5) {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttlMinutes
    })
  }

  /**
   * Set cache for specific endpoint with default TTL
   * @param {string} endpoint - 'filters' | 'productsList' | 'productDetail'
   * @param {string} key
   * @param {any} data
   */
  setWithDefaultTTL(endpoint, key, data) {
    const ttl = this.defaultTTL[endpoint] || 5
    this.set(key, data, ttl)
  }

  /**
   * Get cache entry if valid, null if expired or not found
   * @param {string} key
   * @returns {any|null}
   */
  get(key) {
    const entry = this.cache.get(key)
    
    if (!entry) {
      return null
    }

    if (!this._isValid(entry)) {
      this.cache.delete(key)
      return null
    }

    return entry.data
  }

  /**
   * Clear specific cache entry
   * @param {string} key
   */
  clear(key) {
    this.cache.delete(key)
  }

  /**
   * Clear all cache entries
   */
  clearAll() {
    this.cache.clear()
  }

  /**
   * Clear cache entries matching a pattern
   * @param {string|RegExp} pattern
   */
  clearPattern(pattern) {
    const regex = pattern instanceof RegExp ? pattern : new RegExp(pattern)
    
    for (const [key] of this.cache.entries()) {
      if (regex.test(key)) {
        this.cache.delete(key)
      }
    }
  }

  /**
   * Get cache stats (for debugging)
   * @returns {Object}
   */
  getStats() {
    const entries = Array.from(this.cache.entries())
    const validEntries = entries.filter(([, entry]) => this._isValid(entry))
    const expiredEntries = entries.filter(([, entry]) => !this._isValid(entry))

    if (expiredEntries.length > 0) {
      expiredEntries.forEach(([key]) => this.cache.delete(key))
    }

    return {
      totalEntries: this.cache.size,
      validEntries: validEntries.length,
      expiredEntries: expiredEntries.length,
      keys: validEntries.map(([key]) => key)
    }
  }
}

export const cacheManager = new CacheManager()
